import React, { FC } from 'react';
import TrainingLogPageHeader from '../../components/training-log-page-header/training-log-page-header.component';
import {
  CHART_GUIDE_ITEMS,
  FOCUS_CATEGORIES,
  PERIODIZATION_ITEMS,
  SPORT_SECTIONS,
  TRAINING_ZONE_ROWS,
  TrainingZoneRow,
} from './data-info.constants';
import './data-info.page.css';

interface ColorDotProps {
  color: string;
  size?: 'sm' | 'md';
}

const ColorDot: FC<ColorDotProps> = ({ color, size = 'md' }) => (
  <span className={`data-info-color-dot data-info-color-dot--${size}`} style={{ backgroundColor: color }} aria-hidden />
);

interface LabeledParagraphProps {
  label: string;
  text: string;
}

const LabeledParagraph: FC<LabeledParagraphProps> = ({ label, text }) => (
  <div className="data-info-labeled-paragraph">
    <span className="data-info-label">{label}</span> {text}
  </div>
);

const TrainingZonesTable: FC = () => (
  <table className="data-info-table">
    <thead>
      <tr>
        <th>Zone</th>
        <th>Name</th>
        <th>Effort Level</th>
        <th>Description</th>
      </tr>
    </thead>
    <tbody>
      {TRAINING_ZONE_ROWS.map((row: TrainingZoneRow, index) => (
        <tr
          key={row.zone}
          className={index < TRAINING_ZONE_ROWS.length - 1 ? 'data-info-table-row--bordered' : undefined}
        >
          <td>
            <ColorDot color={row.color} />
            {row.zone}
          </td>
          <td className="data-info-cell-secondary">{row.name}</td>
          <td className="data-info-cell-muted">{row.effort}</td>
          <td className="data-info-cell-secondary">{row.description}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

const DataInfoPage: FC = () => {
  return (
    <div className="data-info-page">
      <TrainingLogPageHeader
        title="Data Info"
        subtitle="A guide to reading and understanding the training data throughout this dashboard"
      />

      <div className="data-info-card">
        <div className="data-info-card-header">
          <div className="data-info-card-title">Training Zones (Z0–Z5)</div>
          <div className="data-info-card-subtitle">
            Heart rate–based intensity zones used to classify every training session
          </div>
        </div>
        <div className="data-info-card-body data-info-card-body--table">
          <TrainingZonesTable />
        </div>
      </div>

      <div className="data-info-card">
        <div className="data-info-card-header">
          <div className="data-info-card-title">Periodization &amp; Season Structure</div>
          <div className="data-info-card-subtitle">How the training year is organized into mesocycles</div>
        </div>
        <div className="data-info-card-body data-info-card-body--text">
          {PERIODIZATION_ITEMS.map((item) => (
            <LabeledParagraph key={item.label} label={item.label} text={item.text} />
          ))}
        </div>
      </div>

      <div className="data-info-card">
        <div className="data-info-card-header">
          <div className="data-info-card-title">Sport Types &amp; Sub-Types</div>
          <div className="data-info-card-subtitle">The four main sport categories and their discipline breakdowns</div>
        </div>
        <div className="data-info-card-body data-info-card-body--text">
          {SPORT_SECTIONS.map((section) => (
            <div key={section.sport} className="data-info-sport-section">
              <div className="data-info-sport-title" style={{ color: section.sportColor }}>
                {section.sport}
              </div>
              <div className="data-info-sport-intro">{section.intro}</div>
              <div className="data-info-subtype-grid">
                {section.subTypes.map((subType) => (
                  <div key={subType.name} className="data-info-subtype-item">
                    <ColorDot color={subType.color} size="sm" />
                    <span className="data-info-subtype-name">{subType.name}</span>
                    {' — '}
                    {subType.description}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="data-info-card">
        <div className="data-info-card-header">
          <div className="data-info-card-title">Training Focus Categories</div>
          <div className="data-info-card-subtitle">How each session is classified by its primary training intent</div>
        </div>
        <div className="data-info-card-body data-info-card-body--text">
          <div className="data-info-focus-grid">
            {FOCUS_CATEGORIES.map((focus) => (
              <div key={focus.name} className="data-info-focus-item">
                <ColorDot color={focus.color} />
                <span className="data-info-focus-name">{focus.name}</span>
                {' — '}
                {focus.description}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="data-info-card">
        <div className="data-info-card-header">
          <div className="data-info-card-title">Reading the Charts</div>
          <div className="data-info-card-subtitle">How to interpret the key visualizations in this dashboard</div>
        </div>
        <div className="data-info-card-body data-info-card-body--text">
          {CHART_GUIDE_ITEMS.map((item) => (
            <LabeledParagraph key={item.label} label={item.label} text={item.text} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default DataInfoPage;
